'use strict';

var FirebaseExt = require('./FirebaseExt');
var FirebaseObject = require('./FirebaseObject');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('FirebaseArray');

function FirebaseArray(ref) {
    this.$ref = ref;
    this.$id = ref.key;
    this.$internalIsLoaded = false;
    this.$items = [];
    this.$loadedPromises = [];
    this.$childAddedListeners = [];
    this.$childRemovedListeners = [];
    this.$childChangedListeners = [];
    this.$destroyListeners = [];

    if (KayleeConnectionState.isWorkingOnline()) {
        ref.orderByKey().on('child_added', this.$childAddedCb, this.$cancelledCb, this);
        ref.orderByKey().on('child_removed', this.$childRemovedCb, this);
        ref.orderByKey().on('child_changed', this.$childChangedCb, this);
        ref.orderByKey().once('value', this.$valueCb, this);
    }

    KayleeUtil.bindPrototypeMethods(FirebaseArray.prototype, this, this.$items);
    log.debug('created ' + ref);
    return this.$items;
}

FirebaseArray.$fromJson = function(data, name, reqProps, exclProps, create) {
    reqProps = reqProps.concat([ '$items', '$internalIsLoaded' ]);
    exclProps = exclProps.concat([ '$nodeType' ]);

    var arr = FirebaseObject.$intFromJson(data, name, reqProps, exclProps, function(data) {
        // FirebaseArrays return the $items array, but we want to augment the
        // underlying object.
        return create(data).$self;
    });

    return arr.$items;
};

FirebaseArray.prototype.$toJson = function(preserveProps, addData) {
    preserveProps = preserveProps.concat([ '$nodeType', '$internalIsLoaded', '$items' ]);
    return FirebaseObject.$intToJson(this, preserveProps, addData);
};

FirebaseArray.prototype.$destroy = function(error) {
    log.debug('destroying ' + this.$ref);

    this.$notifyDestroyListeners();

    if (KayleeConnectionState.isWorkingOnline()) {
        this.$ref.orderByKey().off('child_removed', this.$childRemovedCb, this);
        this.$ref.orderByKey().off('child_added', this.$childAddedCb, this);
    }

    for (var i = 0, ii = this.$items.length; i < ii; i++) {
        this.$notifyChildRemovedListeners(this.$items[i].$id);
    }

    this.$internalIsLoaded = false;
    if (typeof error === 'undefined') {
        this.$rejectLoadedPromises(new Error('array destroyed'));
    }
    else {
        this.$rejectLoadedPromises(error);
    }
    this.$childAddedListeners.length = 0;
    this.$childRemovedListeners.length = 0;
    this.$childChangedListeners.length = 0;
    this.$destroyListeners.length = 0;
    this.$items.length = 0;
    FirebaseExt.update();
};

FirebaseArray.prototype.$childAddedCb = function(snap) {
    var child = JSON.parse(JSON.stringify(snap.val()));
    child.$id = snap.key;
    this.$items.push(child);
    this.$notifyChildAddedListeners(child.$id);
    FirebaseExt.update();
};

FirebaseArray.prototype.$childRemovedCb = function(snap) {
    var index = this.$indexFor(snap.key);

    if (index < 0) {
        log.error('$childRemovedCb: key ' + snap.key + ' not found in array ' +
            'for ' + this.$ref);
        return;
    }

    this.$notifyChildRemovedListeners(snap.key);
    this.$items.splice(index, 1);
    FirebaseExt.update();
};

FirebaseArray.prototype.$childChangedCb = function(snap, prevChildKey) {
    var index = this.$indexFor(snap.key);

    if (index < 0) {
        log.error('$childChangedCb: key ' + snap.key + ' not found in array ' +
            'for ' + this.$ref);
        return;
    }

    var prevChild = this.$items[index];
    var child = JSON.parse(JSON.stringify(snap.val()));
    child.$id = snap.key;
    this.$items[index] = child;

    this.$notifyChildChangedListeners(child.$id, prevChild);
    FirebaseExt.update();
};

FirebaseArray.prototype.$cancelledCb = function(error) {
    //log.debug('$cancelledCb: ' + this.$ref + ', error: ' + error);
    this.$destroy(error);
};

FirebaseArray.prototype.$valueCb = function(snap) {
    this.$internalIsLoaded = true;
    this.$resolveLoadedPromises();
    FirebaseExt.update();
};

FirebaseArray.prototype.$notifyChildAddedListeners = function(key) {
    this.$childAddedListeners.forEach(function(listener) {
        listener(key);
    });
};

FirebaseArray.prototype.$notifyChildRemovedListeners = function(key) {
    this.$childRemovedListeners.forEach(function(listener) {
        listener(key);
    });
};

FirebaseArray.prototype.$notifyChildChangedListeners = function(key, prevChild) {
    this.$childChangedListeners.forEach(function(listener) {
        listener(key, prevChild);
    });
};

FirebaseArray.prototype.$isLoaded = function() {
    return this.$internalIsLoaded;
};

FirebaseArray.prototype.$loaded = function() {
    if (this.$internalIsLoaded) return Promise.resolve(this.$items);

    var deferredPromise = {};
    deferredPromise.promise = new Promise(function(resolve, reject) {
        deferredPromise.resolve = resolve;
        deferredPromise.reject = reject;
    });

    this.$loadedPromises.push(deferredPromise);

    return deferredPromise.promise;
};

FirebaseArray.prototype.$resolveLoadedPromises = function() {
    for (var i = 0; i < this.$loadedPromises.length; i++) {
        this.$loadedPromises[i].resolve(this.$items);
    }

    this.$loadedPromises.length = 0;
};

FirebaseArray.prototype.$rejectLoadedPromises = function(reason) {
    for (var i = 0; i < this.$loadedPromises.length; i++) {
        this.$loadedPromises[i].reject(reason);
    }

    this.$loadedPromises.length = 0;
};

FirebaseArray.prototype.$onChildAdded = function(listener) {
    this.$childAddedListeners.push(listener);

    for (var i = 0; i < this.$items.length; i++) {
        listener(this.$items[i].$id);
    }
};

FirebaseArray.prototype.$offChildAdded = function(listener) {
    for (var i = 0; i < this.$childAddedListeners.length; i++) {
        if (this.$childAddedListeners[i] === listener) {
            this.$childAddedListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseArray.prototype.$onChildRemoved = function(listener) {
    this.$childRemovedListeners.push(listener);
};

FirebaseArray.prototype.$offChildRemoved = function(listener) {
    for (var i = 0; i < this.$childRemovedListeners.length; i++) {
        if (this.$childRemovedListeners[i] === listener) {
            this.$childRemovedListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseArray.prototype.$onChildChanged = function(listener) {
    this.$childChangedListeners.push(listener);
};

FirebaseArray.prototype.$offChildChanged = function(listener) {
    for (var i = 0; i < this.$childChangedListeners.length; i++) {
        if (this.$childChangedListeners[i] === listener) {
            this.$childChangedListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseArray.prototype.$onDestroy = function(listener) {
    this.$destroyListeners.push(listener);
};

FirebaseArray.prototype.$offDestroy = function(listener) {
    for (var i = 0; i < this.$destroyListeners.length; i++) {
        if (this.$destroyListeners[i] === listener) {
            this.$destroyListeners.splice(i, 1);
        }
    }
};

FirebaseArray.prototype.$notifyDestroyListeners = function() {
    for (var i = 0; i < this.$destroyListeners.length; i++) {
        this.$destroyListeners[i]();
    }
};

FirebaseArray.prototype.$indexFor = function(key) {
    var items = this.$items;

    for (var i = 0, ii = items.length; i < ii; i++) {
        if (items[i].$id === key) return i;
    }

    return -1;
};

FirebaseArray.prototype.$recordAt = function(key) {
    var index = this.$indexFor(key);
    if (index < 0) return null;
    return this.$items[index];
};

FirebaseArray.prototype.$waitForChildAdded = function(key) {
    var self = this;
    var child = self.$recordAt(key);

    if (child !== null) {
        return Promise.resolve(child);
    }

    var resolveChildAddedPromise;
    var childAddedPromise = new Promise(function(resolve, reject) {
        resolveChildAddedPromise = resolve;
    });
    var listener = function(addedKey) {
        if (addedKey === key) {
            self.$offChildAdded(listener);
            resolveChildAddedPromise(self.$recordAt(key));
        }
    };

    self.$onChildAdded(listener);
    return childAddedPromise;
};

FirebaseArray.prototype.$waitForChildRemoved = function(key) {
    var self = this;
    var child = self.$recordAt(key);

    if (child === null) {
        return Promise.resolve();
    }

    var resolveChildRemovedPromise;
    var childRemovedPromise = new Promise(function(resolve, reject) {
        resolveChildRemovedPromise = resolve;
    });
    var listener = function(removedKey) {
        if (removedKey === key) {
            self.$offChildRemoved(listener);
            resolveChildRemovedPromise();
        }
    };

    self.$onChildRemoved(listener);
    return childRemovedPromise;
};

module.exports = FirebaseArray;
